import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

import API_URL from '../utils/apiUrl';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Single shared axios instance — headers updated via interceptor
const api = axios.create({
  baseURL: API_URL || undefined,
  withCredentials: true,
});

import { BUILD_ID } from '../buildInfo';

// Request interceptor: always reads the LATEST token from localStorage
api.interceptors.request.use((config) => {
  const currentToken = localStorage.getItem('token');
  if (currentToken) {
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  // Lets the backend detect clients running an outdated build and push a
  // "refresh your browser" notification into their bell.
  config.headers['X-App-Build-Id'] = BUILD_ID;
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // The callback component will handle token extraction and user fetch.
    if (window.location.hash?.includes('session_id=') || 
        window.location.hash?.includes('token=') ||
        window.location.pathname === '/auth/google/success') {
      setLoading(false);
      return;
    }

    // Always check the optional session on bootstrap. Two authentication
    // sources can populate the user:
    //   1. localStorage `token` → axios interceptor adds Bearer header
    //   2. `session_token` cookie (SameSite=None, Secure, HttpOnly) →
    //      sent automatically by the browser, validated by backend
    //
    // This is critical for the GHL iframe deep-link flow — inside a
    // third-party iframe, browsers partition `localStorage` so #1 is
    // unavailable, but cookies (with SameSite=None) follow the user
    // and #2 resolves the session. Anonymous visitors receive user=null,
    // not a failing protected-resource request. /me stays auth-protected.
    try {
      const response = await api.get('/api/auth/session');
      setUser(response.data.user);
      if (!response.data.user) {
        setToken(null);
        localStorage.removeItem('token');
      }
    } catch {
      setUser(null);
      setToken(null);
      // Only clear the localStorage token if it was actually there
      // (avoid removing a key that doesn't exist).
      if (localStorage.getItem('token')) localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, otpCode = null) => {
    const response = await api.post('/api/auth/login', {
      email,
      password,
      otp_code: otpCode
    });
    
    if (response.data.requires_otp) {
      return { requiresOtp: true, message: response.data.message };
    }

    // Challenge kit buyer who never finished VIP onboarding
    if (response.data.funnel_onboarding) {
      return { funnelOnboarding: true, email: response.data.email };
    }
    
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(userData);
    return { 
      requiresOtp: false, 
      user: userData,
      needsPasswordReset: response.data.needs_password_reset || false
    };
  };

  const resetTempPassword = async (email, currentPassword, newPassword) => {
    const response = await api.post('/api/auth/reset-temporary-password', {
      email,
      current_password: currentPassword,
      new_password: newPassword
    });
    
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, plan = 'launchpad', funnelSessionId = null) => {
    const response = await api.post('/api/auth/register', {
      name,
      email,
      password,
      plan,
      funnel_session_id: funnelSessionId,
    });
    
    // New flow: redirect to Stripe checkout
    if (response.data.requires_checkout) {
      return { 
        requiresCheckout: true, 
        checkoutUrl: response.data.checkout_url,
        sessionId: response.data.session_id
      };
    }

    // Fallback for direct registration (invited users, etc.)
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(userData);
    return { requiresCheckout: false, user: userData };
  };

  const completeRegistration = async (sessionId) => {
    const response = await api.get(`/api/auth/register/status/${sessionId}`);
    
    if (response.data.status === 'paid') {
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      localStorage.setItem('token', newToken);
      setUser(userData);
      return { status: 'paid', user: userData };
    }
    
    return { status: response.data.status };
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to our own backend Google OAuth endpoint (not Emergent)
    window.location.href = `${window.location.origin}/api/auth/google/login`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    // Legacy Emergent flow — kept for backward compat
    const response = await api.post('/api/auth/google/exchange', {
      session_id: sessionId
    });
    
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(userData);
    return userData;
  };

  const handleGoogleCallback = async (jwtToken) => {
    // Called from the /auth/google/success route after Google OAuth redirect
    // Store token FIRST so the interceptor picks it up
    localStorage.setItem('token', jwtToken);
    setToken(jwtToken);
    // Fetch user with the new token so ProtectedRoute sees a valid user
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (e) {
      // Token invalid — will redirect to login
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Ignore errors during logout
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const requestOtp = async (email, purpose = 'login') => {
    const response = await api.post('/api/auth/otp/request', { email, purpose });
    return response.data;
  };

  const verifyOtp = async (email, otpCode, purpose = 'login') => {
    const response = await api.post('/api/auth/otp/verify', {
      email,
      otp_code: otpCode,
      purpose
    });
    return response.data;
  };

  const updateProfile = async (data) => {
    const response = await api.put('/api/auth/profile', data);
    setUser(response.data);
    return response.data;
  };

  const updateEmail = async (newEmail, otpCode) => {
    const response = await api.put('/api/auth/email', {
      new_email: newEmail,
      otp_code: otpCode
    });
    setUser(response.data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword, otpCode = null) => {
    const response = await api.put('/api/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
      otp_code: otpCode
    });
    return response.data;
  };

  const verifyEmail = async (otpCode) => {
    const response = await api.post('/api/auth/verify-email', {
      email: user?.email,
      otp_code: otpCode,
      purpose: 'verify_email'
    });
    // Update user's email_verified status
    setUser(prev => ({ ...prev, email_verified: true }));
    return response.data;
  };

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    loading,
    token,
    setUser,
    setToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    login,
    register,
    completeRegistration,
    googleLogin,
    exchangeGoogleSession,
    handleGoogleCallback,
    logout,
    requestOtp,
    verifyOtp,
    updateProfile,
    updateEmail,
    changePassword,
    verifyEmail,
    fetchUser,
    resetTempPassword,
    checkAuth,
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
